import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel, formatRangePretty, formatFYLabel } from "@/lib/useReportRange";
import { exportExcel, exportFinancialPDF } from "@/lib/exporters";

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/reports/trial-balance`, { params }).then((r) => setData(r.data));
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    const rows = [
      [`Period: ${formatRangeLabel(range)}`],
      [],
      ["Ledger", "Group", "Debit", "Credit"],
      ...data.rows.map((r) => [r.name, r.group, r.debit, r.credit]),
      ["Total", "", data.total_debit, data.total_credit],
    ];
    exportExcel(`trial-balance-${active?.name || "report"}`, [{ name: "Trial Balance", rows }]);
  };

  const onPDF = () => {
    if (!data) return;
    const rows = data.rows.map((r) => [r.name, r.group, r.debit ? formatINR(r.debit) : "—", r.credit ? formatINR(r.credit) : "—"]);
    rows.push({ cells: ["TOTAL", "", formatINR(data.total_debit), formatINR(data.total_credit)], type: "grand" });
    exportFinancialPDF(
      `trial-balance-${active?.name || "report"}`, "Trial Balance",
      [{ title: "", headers: ["Ledger", "Group", "Debit (Rs.)", "Credit (Rs.)"], rows }],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(range),
        subtitle: `${formatRangePretty(range)}`,
        date: applied.end,
        place: active?.state || "",
        showSignatures: false,
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Trial Balance"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table" data-testid="table-trial-balance">
            <thead>
              <tr><th>Ledger</th><th>Group</th><th className="text-right">Debit (₹)</th><th className="text-right">Credit (₹)</th></tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {data?.rows.map((r) => (
                <tr key={`${r.id || r.name}-${r.group}`}>
                  <td className="font-medium">
                    {r.id ? (
                      <Link
                        to={`/reports/ledger/${r.id}?start=${applied.start || ""}&end=${applied.end || ""}`}
                        className="text-blue-700 hover:underline"
                        data-testid={`link-ledger-${r.name}`}
                        title="Open this ledger's transaction detail — edits there update this report."
                      >
                        {r.name}
                      </Link>
                    ) : r.name}
                  </td>
                  <td className="text-xs text-gray-500">{r.group}</td>
                  <td className="tx-num">{r.debit ? formatINR(r.debit) : ""}</td>
                  <td className="tx-num">{r.credit ? formatINR(r.credit) : ""}</td>
                </tr>
              ))}
              {data?.closing_stock > 0 && (
                <tr className="bg-blue-50/50">
                  <td className="font-medium">Closing Stock ({(data.valuation_method || "FIFO").replaceAll("_", " ")})</td>
                  <td className="text-xs text-gray-500 italic">Memo — Current Asset</td>
                  <td className="tx-num font-medium text-blue-700">{formatINR(data.closing_stock)}</td>
                  <td className="tx-num"></td>
                </tr>
              )}
              {data && (
                <tr className="font-semibold bg-gray-50 border-t-2 border-gray-300">
                  <td colSpan={2}>Total</td>
                  <td className="tx-num">{formatINR(data.total_debit)}</td>
                  <td className="tx-num">{formatINR(data.total_credit)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
export default function TrialBalance() { return <RequireCompany><Inner /></RequireCompany>; }
